// src/components/resources/calculators/MarketingInsights.tsx

'use client'

import { motion } from 'motion/react'
import Image from 'next/image'
import RiLineChartLine from '~/icons/remix/generated/RiLineChartLine'
import RiPieChartLine from '~/icons/remix/generated/RiPieChartLine'
import RiBarChartBoxLine from '~/icons/remix/generated/RiBarChartBoxLine'
import RiRocketLine from '~/icons/remix/generated/RiRocketLine'
import { mockImages } from '~/lib/shared/mockData'

type ColorKey = 'indigo' | 'blue' | 'purple' | 'emerald'

const insights = [
  {
    title: 'Data-Driven Creative Testing',
    description: 'Optimize your creative testing budget and strategy with statistical confidence. Get actionable insights to improve campaign performance.',
    icon: RiLineChartLine,
    metric: '47%',
    metricLabel: 'Average improvement in ROAS',
    image: mockImages.analytics.wide2,
    color: 'indigo' as ColorKey
  },
  {
    title: 'ROI Optimization',
    description: 'Track and improve your return on ad spend across campaigns with advanced analytics and AI-powered recommendations.',
    icon: RiPieChartLine,
    metric: '2.8x',
    metricLabel: 'Average ROAS improvement',
    image: mockImages.analytics.wide3,
    color: 'blue' as ColorKey
  },
  {
    title: 'Creative Performance',
    description: 'Grade your creative against platform best practices and improve performance with AI-powered insights and recommendations.',
    icon: RiBarChartBoxLine,
    metric: '32%',
    metricLabel: 'Higher engagement rates',
    image: mockImages.creative.wide,
    color: 'purple' as ColorKey
  },
  {
    title: 'Growth Forecasting',
    description: 'Predict future campaign performance and identify growth opportunities using machine learning models.',
    icon: RiRocketLine,
    metric: '89%',
    metricLabel: 'Forecast accuracy',
    image: mockImages.business.wide,
    color: 'emerald' as ColorKey
  }
]

const colors: Record<ColorKey, string> = {
  indigo: 'from-indigo-600/20 text-indigo-600 bg-indigo-600/10 dark:text-indigo-400 dark:bg-indigo-400/10',
  blue: 'from-blue-600/20 text-blue-600 bg-blue-600/10 dark:text-blue-400 dark:bg-blue-400/10',
  purple: 'from-purple-600/20 text-purple-600 bg-purple-600/10 dark:text-purple-400 dark:bg-purple-400/10',
  emerald: 'from-emerald-600/20 text-emerald-600 bg-emerald-600/10 dark:text-emerald-400 dark:bg-emerald-400/10'
}

export function MarketingInsights(): JSX.Element {
  return (
    <section className="relative py-16 sm:py-24 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900/50 dark:to-gray-950">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Improve Your Marketing Performance
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Our calculators help marketers make better decisions and achieve better results
            </p>
          </motion.div>
        </div>

        <div className="grid gap-8 lg:grid-cols-2 xl:grid-cols-4">
          {insights.map((insight, index) => (
            <motion.div
              key={insight.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="relative group"
            >
              {/* Image with Gradient Overlay */}
              <div className="relative h-48 overflow-hidden rounded-2xl mb-8 transition-transform duration-300 group-hover:scale-[1.02]">
                <Image
                  src={insight.image}
                  alt={insight.title}
                  fill
                  className="object-cover transition-transform duration-300 group-hover:scale-105"
                  sizes="(min-width: 1280px) 25vw, (min-width: 768px) 50vw, 100vw"
                />
                <div className={`absolute inset-0 bg-gradient-to-tr ${colors[insight.color].split(' ')[0]} to-transparent`} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-white">
                      {insight.metric}
                    </span>
                    <span className="text-sm text-white/90">
                      {insight.metricLabel}
                    </span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div>
                <div className={`mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg ${colors[insight.color].split(' ').slice(1).join(' ')}`}>
                  <insight.icon className={`h-6 w-6 ${colors[insight.color].split(' ')[1]}`} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {insight.title}
                </h3>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                  {insight.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}